
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute } from 'vue-router'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import NewModal from '@/common/new-components/NewModal/index.vue'
import TtnFiles from '@/modules/TTNModule/components/TtnFiles/index.vue'
import TtnInvoices from '@/modules/TTNModule/components/TtnInvoices/index.vue'

// @module components
import TtnsDetailedTMSRow from '@/modules/TMSModule/components/TtnsDetailedTMSRow/index.vue'
import TtnTimer from '@/modules/TMSModule/components/TtnTimer/index.vue'
import TtnEditModal from '@/modules/TMSModule/components/TtnEditModal/index.vue'

// @types
import { LoadDefectActPayload, TtnDetailedTMS, TtnTMS } from '@/new-services/types/tms'

// @services
import tmsAPI from '@/new-services/tms.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import { TRIPS_STATUSES } from '@/core/constants/tms.constants'

import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useFormData } from '@/common/composable/useFormData'

import { formatDateWithTimeFromBackend, formatDateFromBackend } from '@/common/helpers/formatDate'

const TABLE_COLUMNS = [
    {
        key: 'index',
        name: '№',
    },
    {
        key: 'invoiceNumber',
        name: 'Номер накладной',
    },
    {
        key: 'company',
        name: 'Клиент',
    },
    {
        key: 'cubature',
        name: 'Кубатура',
    },
    {
        key: 'weight',
        name: 'Физ. вес',
    },
    {
        key: 'volumeWeight',
        name: 'Объемный вес'
    },
    {
        key: 'places',
        name: 'Количество мест',
    },
]

const TABS = [
    { id: 'general', name: 'Общая информация' },
    { id: 'files', name: 'Акт об обнаружении недостатков' },
]

export default defineComponent({
    components: {
        PageTitle,
        Loader,
        NewButton,
        NewTable,
        'ttns-detailed-tms-row': TtnsDetailedTMSRow,
        TtnTimer,
        Cheap,
        FileReader,
        NewModal,
        TtnFiles,
        TtnInvoices,
        TtnEditModal,
    },
    setup() {
        const notifications = useNotifications()
        const route = useRoute()
        const ttnId = Number(route.params.id as string)

        const state = reactive({
            is_loading: false,
            is_failed: false,
            //
            is_files: false,
            is_files_loading: false,
            actFiles: [] as File[],
            productFiles: [] as File[],
            currentTab: 'general',
            //
            ttn: null as TtnDetailedTMS | null,
            ttnTwo: null as TtnDetailedTMS | null,
        })

        const edit_state = reactive({
            is_open: false,
            item: null as TtnTMS | null,
        })

        const validation$ = useVuelidate(
            {
                actFiles: { required },
            },
            state
        )

        //

        const loadTtn = async () => {
            try {
                state.is_loading = true

                const response = await tmsAPI.getTtn(ttnId)
                const responseTwo = await tmsAPI.getTtn(ttnId, 1)

                state.ttn = response.data.data
                state.ttnTwo = responseTwo.data.data

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении детальной информации произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const { getFormData } = useFormData()

        const loadFiles = async () => {
            validation$.value.$touch()
            if (validation$.value.$invalid) return

            try {
                state.is_files_loading = true

                const payload: LoadDefectActPayload = {
                    actFiles: state.actFiles,
                }

                if (state.productFiles.length) {
                    payload.productFiles = state.productFiles
                }

                await tmsAPI.loadDefectAct(ttnId, getFormData(payload))
                toggleFiles()
                loadTtn()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке файлов произошла ошибка',
                })
            } finally {
                state.is_files_loading = false
            }
        }

        const toggleFiles = () => {
            state.actFiles = []
            state.productFiles = []
            state.is_files = !state.is_files
        }

        const isTabSelected = (tab: { id: string }) => state.currentTab === tab.id

        const selectTab = (tab: { id: string }) => {
            if (isTabSelected(tab)) return
            state.currentTab = tab.id
        }

        //

        const isTabs = computed(
            () =>
                state.ttn &&
                ((state.ttn.defectActs && state.ttn.defectActs.length) ||
                    (state.ttn.defectProducts && state.ttn.defectProducts.length))
        )
        const isGeneral = computed(() => state.currentTab === 'general')
        const isFiles = computed(() => state.currentTab === 'files')

        const pageTitle = computed(() => {
            if (state.ttn) {
                const { ttnNumber, fromCity, toCity, createdAt } = state.ttn
                return `ТТН №${ttnNumber} ${fromCity.name} - ${toCity.name} От ${formatDateWithTimeFromBackend(createdAt)}`
            }

            return 'ТТН №... ... от ...'
        })

        const getCheapClass = computed(() => {
            let className = 'small'

            if (state.ttn && state.ttn.status) {
                className += TRIPS_STATUSES[state.ttn.status.id - 1]
            }

            return className
        })

        const openEdit = () => {
            edit_state.is_open = true
        }

        const closeEdit = () => {
            edit_state.is_open = false
            loadTtn()
        }

        //

        onBeforeMount(loadTtn)

        //

        return {
            state,
            edit_state,
            validation$,
            //
            loadTtn,
            toggleFiles,
            loadFiles,
            selectTab,
            isTabSelected,
            TABS,
            isTabs,
            isGeneral,
            isFiles,
            closeEdit,
            openEdit,
            //
            pageTitle,
            ttnId,
            getCheapClass,
            //
            TABLE_COLUMNS,
            formatDateWithTimeFromBackend,
            formatDateFromBackend,
        }
    },
})
